<template>
  <div
    id="interactive"
    class="viewport scanner"
    :style="{
      minWidth: fitContainer ? '0' : `${videoWidth || readerSize.width}px`,
      minHeight: fitContainer ? '0' : `${videoHeight || readerSize.height}px`,
      aspectRatio: scannerAspectRatio,
    }"
  >
    <!-- :style="{ minWidth: `${readerSize.width}px`, minHeight: `${readerSize.height}px` }" -->
    <video />
    <canvas class="drawingBuffer" />
  </div>
</template>

<script setup lang="ts">
import type {
  QuaggaJSConfigObject,
  QuaggaJSRectSize,
  QuaggaJSResultObject,
  QuaggaJSxy,
} from "@ericblade/quagga2";
import Quagga from "@ericblade/quagga2";
import QrCodeReader from "./qr_plugin";
import {
  ref,
  watch,
  onMounted,
  onBeforeUnmount,
  computed,
  defineComponent,
} from "vue";
import type { CssColor } from "@/helper/cssColor";
import type { QRCode } from "jsqr";
import { isObjectWithKeys } from "@/helper/isObjectWithKeys";
import type { BarcodeScanPayload } from "./types";

const isError = function (exception: any): exception is Error {
  return (
    typeof exception == "object" &&
    exception !== null &&
    "name" in exception &&
    typeof exception.name === "string" &&
    "message" in exception &&
    typeof exception.message === "string"
  );
};

type AspectRatio = {
  min: number;
  max: number;
};
type ReaderSize = {
  width: number;
  height: number;
};

type WrongPosType = any[];
type WrongSizeType = QuaggaJSRectSize;
const wrongPosTypeGuard = (o: any): o is WrongPosType =>
  typeof o === "object" && typeof o.x === "number" && typeof o.y === "number";
const wrongSizeTypeGuard = (o: any): o is WrongSizeType =>
  typeof o === "object" && typeof o.x === "number" && typeof o.y === "number";
const convertWrongPosType = function (o: QuaggaJSxy): WrongPosType {
  if (!wrongPosTypeGuard(o)) {
    throw "Should be that type";
  }
  return o;
};
const convertWrongSizeType = function (o: QuaggaJSxy): WrongSizeType {
  if (!wrongSizeTypeGuard(o)) {
    throw "Should be that type";
  }
  return o;
};

type QrCodeResultObject = QRCode & {
  codeResult: {
    code: string;
    format: string;
  };
};

function isQuaggaJSResultObject(o: any): o is QuaggaJSResultObject {
  return isObjectWithKeys(o, [
    "codeResult",
    "line",
    "angle",
    "pattern",
    "box",
    "boxes",
  ]);
}
function isQrCodeResultObject(o: any): o is QrCodeResultObject {
  return isObjectWithKeys(o, [
    "codeResult",
    "binaryData",
    "data",
    "chunks",
    "version",
    "location",
  ]);
}

type CallbackFunctionPayload = QuaggaJSResultObject | QrCodeResultObject;
type CallbackFunctionWithPayload = (
  data: CallbackFunctionPayload
) => void | undefined;

defineComponent({
  name: "QuaggaScanner",
});

const props = withDefaults(
  defineProps<{
    onDetected?: CallbackFunctionWithPayload;
    onProcessed?: CallbackFunctionWithPayload;
    readerTypes?: string[];
    readerSize?: ReaderSize;
    aspectRatio?: AspectRatio;
    facingMode?: string;
    fitContainer?: boolean;
    anticipationWrapColor?: CssColor;
    guessBoxColor?: CssColor;
    scanColor?: CssColor;
  }>(),
  {
    readerTypes: () => ["code_128_reader", "qrcode"],
    readerSize: () => ({
      width: 640,
      height: 480,
    }),
    aspectRatio: () => ({
      min: 1,
      max: 2,
    }),
    facingMode: "environment",
    fitContainer: false,
    anticipationWrapColor: "springgreen",
    guessBoxColor: "springgreen",
    scanColor: "springgreen",
  },
);
const emit = defineEmits<{
  (e: "scan", code: string): void;
  (e: "scan-detail", payload: BarcodeScanPayload): void;
  (e: "frame-size", size: ReaderSize): void;
}>();

const canvas = ref(null); // assigned in the html via `ref="canvas"`.
const videoWidth = ref(0);
const videoHeight = ref(0);
const scannerAspectRatio = computed((): string => {
  const width = videoWidth.value || props.readerSize.width;
  const height = videoHeight.value || props.readerSize.height;
  return `${width} / ${height}`;
});

const lastCode = ref<string | undefined>(undefined);

const defaultOnFrame: CallbackFunctionWithPayload = (
  result: CallbackFunctionPayload
) => {
  let drawingCtx = Quagga.canvas.ctx.overlay;
  let drawingCanvas = Quagga.canvas.dom.overlay;
  updateFrameSize({
    width: Quagga.canvas.dom.overlay.width,
    height: Quagga.canvas.dom.overlay.height,
  });

  if (result) {
    // so basically both method arguments are wrongly typed. Code expects an object {x: int, y: int} for both,
    // but asks for different stuff in the typing.

    // draw anticipation rectangle, as we have at least some result (?)
    drawingCtx.clearRect(
      0,
      0,
      videoWidth.value ?? parseInt(drawingCanvas.getAttribute("width") ?? "0"),
      videoHeight.value ?? parseInt(drawingCanvas.getAttribute("height") ?? "0"),
    );
    const size: QuaggaJSxy = {
      x: videoWidth.value - 4,
      y: videoHeight.value - 4,
    };
    Quagga.ImageDebug.drawRect(
      convertWrongPosType({ x: 2, y: 2 }),
      convertWrongSizeType(size),
      drawingCtx,
      {
        color: props.anticipationWrapColor,
        lineWidth: 2,
      }
    );

    if (typeof result === "object" && "boxes" in result && result.boxes) {
      result.boxes
        .filter(function (box) {
          return box !== result.box;
        })
        .forEach(function (box) {
          Quagga.ImageDebug.drawPath(box, { x: 0, y: 1 }, drawingCtx, {
            color: props.guessBoxColor,
            lineWidth: 2,
          });
        });
    }
    if (typeof result === "object" && "box" in result && result.box) {
      Quagga.ImageDebug.drawPath(result.box, { x: 0, y: 1 }, drawingCtx, {
        color: props.scanColor,
        lineWidth: 2,
      });
    }
    if (typeof result === "object" && "line" in result && result.line) {
      Quagga.ImageDebug.drawPath(result.line, { x: 0, y: 1 }, drawingCtx, {
        color: props.scanColor,
        lineWidth: 4,
      });
    }
    if (typeof result === "object" && "location" in result && result.location) {
      Quagga.ImageDebug.drawPath(
        [
          result.location.topLeftCorner,
          result.location.topRightCorner,
          result.location.bottomRightCorner,
          result.location.bottomLeftCorner,
        ],
        { x: "x", y: "y" },
        drawingCtx,
        {
          color: props.guessBoxColor,
          lineWidth: 2,
        }
      );
      // draw finder pattern box
      if (result.location.bottomRightAlignmentPattern) {
        drawingCtx.beginPath();
        drawingCtx.arc(
          result.location.bottomRightAlignmentPattern.x,
          result.location.bottomRightAlignmentPattern.y,
          10,
          0,
          Math.PI * 2,
        ); // full circle
        drawingCtx.fillStyle = props.scanColor;
        drawingCtx.closePath();
      }
      Quagga.ImageDebug.drawPath(
        [
          result.location.topLeftFinderPattern,
          result.location.topRightFinderPattern,
          ...(
            result.codeResult?.format === "qr_code"
            ? []
            : [
                {
                  x: result.location.bottomLeftFinderPattern.x,
                  y: result.location.topRightFinderPattern.y,
                }
              ]
          ),
          result.location.bottomLeftFinderPattern,
        ],
        { x: "x", y: "y" },
        drawingCtx,
        { color: props.scanColor, lineWidth: 2 }
      );
    }
  } else if (drawingCtx) {
    // console.log("BarcodeScanner: no result but drawing context", { result });
    drawingCtx.clearRect(0, 0, videoWidth.value, videoHeight.value);
  }
};
const defaultOnScan: CallbackFunctionWithPayload = (
  data: CallbackFunctionPayload
): void => {
  console.log(`scanned code ${data.codeResult.code}: `, data);
};

const onProcessed = computed((): CallbackFunctionWithPayload => {
  return props.onProcessed !== undefined
    ? props.onProcessed
    : defaultOnFrame;
});
const onDetected = computed((): CallbackFunctionWithPayload => {
  return props.onDetected !== undefined ? props.onDetected : defaultOnScan;
});

function updateFrameSize(size: ReaderSize): void {
  if (!size.width || !size.height) {
    return;
  }
  if (videoWidth.value === size.width && videoHeight.value === size.height) {
    return;
  }
  videoWidth.value = size.width;
  videoHeight.value = size.height;
  emit("frame-size", size);
}

function getScanFormat(data: CallbackFunctionPayload): string | null {
  return typeof data.codeResult?.format === "string"
    ? data.codeResult.format
    : null;
}

function getBarcodeType(format: string | null): BarcodeScanPayload["barcodeType"] {
  if (format?.toLowerCase().replaceAll("_", "").includes("qr")) {
    return "qr-code";
  }
  return "barcode";
}

function getScanPayload(data: CallbackFunctionPayload): BarcodeScanPayload {
  const format = getScanFormat(data);
  return {
    code: data.codeResult.code,
    format,
    barcodeType: getBarcodeType(format),
  };
}

function onProcessedWhichFakesOnDetected(
  data: CallbackFunctionPayload
): void {
  onProcessed.value(data);

  if (data && data.codeResult && data.codeResult.code) {
    const code = data.codeResult.code;
    console.log('BarcodeScanner: onProcessedWhichFakesOnDetected with result data!', { data });

    if (code && code !== lastCode.value) {
      lastCode.value = code;
      emit("scan", code);
      emit("scan-detail", getScanPayload(data));
    }
    onDetected.value(data);
  }
}

const quaggaState = computed((): QuaggaJSConfigObject => {
  return {
    inputStream: {
      type: "LiveStream",
      constraints: {
        width: { min: props.readerSize.width },
        height: { min: props.readerSize.height },
        facingMode: props.facingMode,
        aspectRatio: { min: 1, max: 2 },
      },
    },
    locator: {
      patchSize: "medium",
      halfSample: true,
    },
    numOfWorkers: 2,
    frequency: 10,
    decoder: {
      readers: props.readerTypes,
    },
    locate: true,
  };
});

watch(
  onDetected,
  (
    oldValue: CallbackFunctionWithPayload,
    newValue: CallbackFunctionWithPayload
  ) => {
    if (oldValue) Quagga.offDetected(oldValue);
    if (newValue) Quagga.onDetected(newValue);
  }
);

watch(
  onProcessed,
  (
    oldValue: CallbackFunctionWithPayload,
    newValue: CallbackFunctionWithPayload
  ) => {
    if (oldValue) Quagga.offProcessed(oldValue);
    if (newValue) Quagga.onProcessed(newValue);
  }
);

onMounted(() => {
  try {
    Quagga.registerReader("qrcode", QrCodeReader);
  } catch (e) {
    if (isError(e) && e.message === "cannot register existing reader") {
      console.log(`Reader ${"qrcode"} already registered`);
    } else {
      console.warn(`failed to register reader ${"qrcode"}`, e);
    }
  }
  Quagga.init(quaggaState.value, function (err) {
    if (err) {
      return console.error(err);
    }
    Quagga.start();
    updateFrameSize({
      width: Quagga.canvas.dom.overlay.width,
      height: Quagga.canvas.dom.overlay.height,
    });
    console.log("STARTED Quagga");
  });
  Quagga.onProcessed(onProcessedWhichFakesOnDetected);
});

// this was `destroyed` before, so basically `onUnmount` not the `before` variant.
onBeforeUnmount(() => {
  Quagga.offProcessed(onProcessedWhichFakesOnDetected);
  Quagga.stop();
  console.log("STOPPED Quagga");
});
</script>

<style scoped>
.viewport {
  position: relative;
}

.viewport.scanner {
  max-height: 100%;
  max-width: 100%;
}

.viewport canvas,
.viewport video {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  right: 0;
  height: 100%;
  width: 100%;
}
</style>
